import { Link as LinkIcon } from '@hendrx/icons/Link';
import { TextBold } from '@hendrx/icons/TextBold';
import { TextItalic } from '@hendrx/icons/TextItalic';
import { TextUnderline } from '@hendrx/icons/TextUnderline';
import { Unlink } from '@hendrx/icons/Unlink';
import { Button, Divider, Flex, Stack, Text, Title } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import CharacterCount from '@tiptap/extension-character-count';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { Editor, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from 'shared/components/layouts/Modal';
import { rem } from 'shared/utils/rem';

import classes from './Tooltip.module.scss';
import { FontFamilySelect } from '../../../../../components/richTexts/RichTextToolbar/FontFamilySelect';
import { InsertImage } from '../../../../../components/richTexts/RichTextToolbar/InsertImage';
import { TextColorPicker } from '../../../../../components/richTexts/RichTextToolbar/TextColorPicker';
import { TextFontSizeSelect } from '../../../../../components/richTexts/RichTextToolbar/TextFontSizeSelect';
import { FontFamily } from '../../fontFamily';
import { FontSize } from '../../fontSize';
import { Heading } from '../../heading';
import { Image } from '../../image';

type TooltipModalProps = {
  content: string;
  onClose: (content: string) => void;
};

const CHARACTER_LIMIT = 250;

export function TooltipEditModal(props: TooltipModalProps) {
  const { content, onClose } = props;
  const { t } = useTranslation('utils.tooltipEditModal');
  const [, setEditorContentRef] = useState<HTMLDivElement | null>(null);

  const editor = useEditor({
    extensions: [
      Color,
      FontFamily,
      FontSize,
      Image,
      StarterKit.configure({ heading: false }),
      Placeholder.configure({ placeholder: t('placeholder') }),
      TextStyle,
      Underline,
      Link,
      CharacterCount.configure({ limit: CHARACTER_LIMIT }),
      Heading
    ],
    content,
    immediatelyRender: false,
    shouldRerenderOnTransaction: true
  });

  return (
    <Modal
      opened={true}
      onClose={() => onClose(content)}
      withinPortal={true}
      dataTestPrefix="tooltip-edit-modal"
      size="auto"
      classNames={{
        content: classes.modal,
        body: classes.modalBody,
        header: classes.modalHeader
      }}
      closeOnClickOutside={false}
      padding={`${rem(16)} ${rem(24)}`}
      closeButtonProps={{ className: classes.closeButton, iconSize: 24 }}
    >
      <div className={classes.modalBodyInner}>
        <Title order={3} ta="center">
          {t('title')}
        </Title>
        <Stack gap={0}>
          <Stack gap={0}>
            <RichTextEditor editor={editor}>
              <InlineToolbar editor={editor} />
              <RichTextEditor.Content ref={setEditorContentRef} />
            </RichTextEditor>
          </Stack>
          <Text color="dark.3" className={classes.editorCounter}>
            {editor?.storage.characterCount.characters()}/{CHARACTER_LIMIT}
          </Text>
        </Stack>
        <Flex className={classes.buttonGroup}>
          <Button variant="subtle" color="gray" size="compact-lg" onClick={() => onClose(content)}>
            {t('cancel')}
          </Button>
          <Button
            size="compact-lg"
            disabled={editor?.storage.characterCount.characters() === 0}
            onClick={() => onClose(editor?.getHTML() ?? '')}
          >
            {t('save')}
          </Button>
        </Flex>
      </div>
    </Modal>
  );
}

type InlineToolbarProps = {
  editor: Editor | null;
};

function InlineToolbar(props: InlineToolbarProps) {
  const { editor } = props;
  const { t } = useTranslation('utils.tooltipEditModal');
  return (
    <RichTextEditor.Toolbar className={classes.toolbarRoot} sticky stickyOffset={60}>
      <RichTextEditor.ControlsGroup className={classes.controlsGroup}>
        <RichTextEditor.Control>
          <TextColorPicker editor={editor} withinPortal={true} />
        </RichTextEditor.Control>
        <RichTextEditor.Underline title={t('underline') ?? ''} icon={TextUnderline} />
        <RichTextEditor.Italic title={t('italic') ?? ''} icon={TextItalic} />
        <RichTextEditor.Bold title={t('bold') ?? ''} icon={TextBold} />
      </RichTextEditor.ControlsGroup>
      <Divider className={classes.divider} />
      <RichTextEditor.ControlsGroup className={classes.controlsGroup}>
        <InsertImage editor={editor} />
        <RichTextEditor.Link title={t('link') ?? ''} icon={LinkIcon} />
        {editor?.isActive('link') && <RichTextEditor.Unlink icon={Unlink} title={t('unlink') ?? ''} />}
      </RichTextEditor.ControlsGroup>
      <Divider className={classes.divider} />
      <RichTextEditor.ControlsGroup className={classes.controlsGroup}>
        <TextFontSizeSelect editor={editor} />
      </RichTextEditor.ControlsGroup>
      <Divider className={classes.divider} />
      <RichTextEditor.ControlsGroup className={classes.controlsGroup}>
        <FontFamilySelect editor={editor} />
      </RichTextEditor.ControlsGroup>
    </RichTextEditor.Toolbar>
  );
}
